<script setup lang="ts">
import dayjs from 'dayjs'
import { Check, ChevronDown, Gift, Ticket } from 'lucide-vue-next'
import CustomerOrderPartnerAvatar from './CustomerOrderPartnerAvatar.vue'

import type { CustomerOrder } from '@/types/customer/Order'
import {
  getChannelChip,
  getStatusChip,
  hasDiscount,
  hasDiscountsOrRewards
} from '@/views/customer/utils/customerOrders'

const props = defineProps<{
  order: CustomerOrder
}>()

const { t, locale } = useI18n()
const { formatCurrency } = useNumber()

const isOpen = ref(false)

const orderedAt = computed(() => {
  const date = dayjs(props.order.orderedAt).locale(locale.value)

  return `${date.format('LL')} · ${date.format('LT')}`
})

const channelChip = computed(() => getChannelChip(props.order.channel?.slug))
const statusChip = computed(() => getStatusChip(props.order.status?.slug))

const pointsLabel = computed(() => {
  const points = props.order.pointsWon ?? 0

  return t('pages.customer.customerDetailPage.orders.points', { points: points > 0 ? `+${points}` : points })
})

const isMerged = computed(() => props.order.sources.length > 1)

const formatTime = (date: string) => dayjs(date).locale(locale.value).format('LT')

const subtitleParts = computed(() =>
  [
    { text: props.order.partners.map((partner) => partner.name).join(' + '), isPartner: true },
    { text: props.order.store?.name, isPartner: false },
    {
      text: props.order.orderNumber
        ? t('pages.customer.customerDetailPage.orders.orderNumber', { number: props.order.orderNumber })
        : null,
      isPartner: false
    }
  ].filter((part) => !!part.text)
)

const formatCents = (amount: number) => formatCurrency(amount / 100)
</script>

<template>
  <Card class="gap-0 overflow-hidden py-0">
    <Collapsible v-model:open="isOpen">
      <CollapsibleTrigger
        class="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-stone-50 sm:px-6"
      >
        <div class="hidden shrink-0 -space-x-3 sm:flex">
          <CustomerOrderPartnerAvatar
            v-for="(partner, index) in order.partners.slice(0, 2)"
            :key="partner.slug"
            :partner="partner"
            class="ring-2 ring-white"
            :style="{ zIndex: 2 - index }"
          />
          <CustomerOrderPartnerAvatar v-if="order.partners.length === 0" :partner="null" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-semibold text-foreground">{{ orderedAt }}</span>
            <BaseChip
              size="sm"
              :color="channelChip.color"
              :text="t(`pages.customer.customerDetailPage.orders.${channelChip.labelKey}`)"
            />
            <BaseChip
              v-if="statusChip"
              size="sm"
              :color="statusChip.color"
              :text="t(`pages.customer.customerDetailPage.orders.${statusChip.labelKey}`)"
            />
            <AppTooltip v-if="isMerged" :text="t('pages.customer.customerDetailPage.orders.merged.tooltip')">
              <BaseChip
                size="sm"
                color="slate"
                :text="t('pages.customer.customerDetailPage.orders.merged.chip', { count: order.sources.length })"
              />
            </AppTooltip>
          </div>
          <p class="truncate text-sm text-muted-foreground">
            <template v-for="(part, index) in subtitleParts" :key="index">
              <span v-if="index > 0"> · </span>
              <span :class="{ 'font-medium text-foreground': part.isPartner }">{{ part.text }}</span>
            </template>
          </p>
        </div>

        <div class="flex shrink-0 flex-col items-end gap-1">
          <div class="flex items-baseline gap-2">
            <span v-if="hasDiscount(order)" class="text-xs text-muted-foreground line-through">
              {{ formatCents(order.totalAmountWithVat) }}
            </span>
            <span class="text-sm font-semibold text-foreground">{{ formatCents(order.paidAmountWithVat) }}</span>
          </div>
          <BaseChip size="sm" :color="(order.pointsWon ?? 0) < 0 ? 'red' : 'violet'" :text="pointsLabel" />
        </div>

        <ChevronDown
          class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
        />
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div class="space-y-4 border-t px-4 py-4 sm:px-6">
          <section>
            <h4 class="mb-2 text-sm font-medium text-foreground">
              {{ t('pages.customer.customerDetailPage.orders.detail.products') }}
            </h4>
            <ul v-if="order.lines.length > 0" class="space-y-1.5">
              <li v-for="line in order.lines" :key="line.id">
                <div class="flex items-baseline justify-between gap-4 text-sm">
                  <span class="text-foreground">
                    <span class="font-medium">{{ line.quantity }} ×</span>
                    {{ line.label ?? t('pages.customer.customerDetailPage.orders.detail.unknownProduct') }}
                  </span>
                  <span class="shrink-0 text-foreground">{{ formatCents(line.amountWithVat) }}</span>
                </div>
                <ul v-if="line.lines?.length" class="mt-1 space-y-1 border-l pl-3 ml-1">
                  <li
                    v-for="subLine in line.lines"
                    :key="subLine.id"
                    class="flex items-baseline justify-between gap-4 text-sm text-muted-foreground"
                  >
                    <span>
                      {{ subLine.quantity }} ×
                      {{ subLine.label ?? t('pages.customer.customerDetailPage.orders.detail.unknownProduct') }}
                    </span>
                    <span v-if="subLine.amountWithVat" class="shrink-0">{{ formatCents(subLine.amountWithVat) }}</span>
                  </li>
                </ul>
              </li>
            </ul>
            <p v-else class="text-sm text-muted-foreground">
              {{ t('pages.customer.customerDetailPage.orders.detail.noLines') }}
            </p>
          </section>

          <template v-if="hasDiscountsOrRewards(order)">
            <Separator />
            <section>
              <h4 class="mb-2 text-sm font-medium text-foreground">
                {{ t('pages.customer.customerDetailPage.orders.detail.discounts') }}
              </h4>
              <ul class="space-y-1.5 text-sm">
                <li
                  v-for="coupon in order.coupons"
                  :key="`coupon-${coupon.id}`"
                  class="flex items-baseline justify-between gap-4"
                >
                  <span class="inline-flex items-center gap-2 text-foreground">
                    <Ticket class="size-4 text-muted-foreground" />
                    {{ t('pages.customer.customerDetailPage.orders.detail.coupon', { code: coupon.code }) }}
                  </span>
                  <span class="shrink-0 text-foreground">−{{ formatCents(coupon.discountAmount) }}</span>
                </li>
                <li v-for="reward in order.rewards" :key="`reward-${reward.id}`" class="flex items-baseline gap-2">
                  <span class="inline-flex items-center gap-2 text-foreground">
                    <Gift class="size-4 text-muted-foreground" />
                    <span>
                      <span v-if="reward.quantity > 1" class="font-medium">{{ reward.quantity }} ×</span>
                      {{ reward.name }}
                    </span>
                  </span>
                </li>
                <li v-if="hasDiscount(order)" class="flex items-baseline justify-between gap-4 font-medium">
                  <span class="text-foreground">{{
                    t('pages.customer.customerDetailPage.orders.detail.totalDiscount')
                  }}</span>
                  <span class="shrink-0 text-foreground">−{{ formatCents(order.discountAmount) }}</span>
                </li>
              </ul>
            </section>
          </template>

          <template v-if="isMerged">
            <Separator />
            <section>
              <h4 class="mb-2 text-sm font-medium text-foreground">
                {{ t('pages.customer.customerDetailPage.orders.detail.sources') }}
              </h4>
              <ul class="space-y-2 text-sm">
                <li v-for="source in order.sources" :key="source.id" class="flex items-center justify-between gap-4">
                  <span class="flex min-w-0 items-center gap-2">
                    <CustomerOrderPartnerAvatar :partner="source.partner" size="sm" />
                    <span class="truncate text-foreground">
                      <span class="font-medium">{{
                        source.partner?.name ?? t('pages.customer.customerDetailPage.orders.detail.unknownPartner')
                      }}</span>
                      <span class="text-muted-foreground">
                        ·
                        {{
                          t(`pages.customer.customerDetailPage.orders.${getChannelChip(source.channel?.slug).labelKey}`)
                        }}
                        <template v-if="source.orderNumber">
                          ·
                          {{
                            t('pages.customer.customerDetailPage.orders.orderNumber', { number: source.orderNumber })
                          }}
                        </template>
                        ·
                        {{
                          t('pages.customer.customerDetailPage.orders.detail.receivedAt', {
                            time: formatTime(source.receivedAt)
                          })
                        }}
                      </span>
                    </span>
                  </span>
                  <span
                    v-if="source.pointsCredited !== 0"
                    class="inline-flex shrink-0 items-center gap-1 font-medium text-green-700"
                  >
                    <Check class="size-4" />
                    {{
                      t('pages.customer.customerDetailPage.orders.detail.pointsCredited', {
                        points: source.pointsCredited
                      })
                    }}
                  </span>
                  <span v-else class="shrink-0 text-muted-foreground">
                    {{ t('pages.customer.customerDetailPage.orders.detail.mergedWithoutPoints') }}
                  </span>
                </li>
              </ul>
            </section>
          </template>

          <Separator />
          <section class="flex items-baseline justify-between gap-4 text-sm">
            <span class="font-medium text-foreground">
              {{ t('pages.customer.customerDetailPage.orders.detail.pointsWon') }}
            </span>
            <span class="font-semibold text-foreground">{{ pointsLabel }}</span>
          </section>
        </div>
      </CollapsibleContent>
    </Collapsible>
  </Card>
</template>
